import { useMemo } from "react";
import { Avatar } from "@heroui/react";
import colors from "tailwindcss/colors";

import { hexToHSL, HSLToHex, useSmoothGradient } from "../@components/SmoothGradient";
import { ensureDynamicAccentClasses } from "../RenderHelpers";
import { Logo } from "../logo";

const DEFAULT_BUTTON_CLASSES = "bg-white/90 text-slate-900 border border-transparent";
const LOGO_SIZE_VARIANTS = {
    raw: {
        container: "",
        icon: "h-8 w-8",
        radius: "full",
        isBordered: false,
        avatarBorder: "",
        avatarFrame: "",
    },
    sm: {
        container: "h-6 w-6",
        icon: "h-5 w-5",
        avatarBorder: "border-1 border-white",
        avatarFrame: "shadow-lg",
        radius: "sm",
        isBordered: false
    },
    md: {
        container: "h-12 w-12",
        icon: "h-8 w-8",
        avatarBorder: "border-3 border-white",
        avatarFrame: "shadow-lg",
        radius: "lg",
        isBordered: false
    },
    lg: {
        container: "h-16 w-16",
        icon: "h-10 w-10",
        avatarBorder: "border-4 border-white",
        avatarFrame: "shadow-lg",
        radius: "lg",
        isBordered: true
    },
};

function resolveColorToHex(value) {
    if (!value || typeof value !== "string") {
        return null;
    }
    if (value.startsWith("#")) {
        return value;
    }
    const scale = colors[value];
    if (scale && typeof scale === "object") {
        return scale[500] ?? null;
    }
    return null;
}

export default function useWorkspaceBranding(workspace, options = {}) {
    const {
        fallbackColor,
        gradientStrength: gradientStrengthOption = 500,
        logoSize = "lg",
    } = options;

    const baseColor = workspace?.color ?? fallbackColor ?? "slate";
    const { gradient: workspaceGradient, values: workspaceGradientValues } = useSmoothGradient(baseColor, gradientStrengthOption);
    const logoSizeVariant = LOGO_SIZE_VARIANTS[logoSize] ?? LOGO_SIZE_VARIANTS.lg;

    const [accentColor, secondaryColor] = useMemo(() => {
        if (!workspace) {
            return [null, null];
        }
        const selected = workspace.brandColor ?? workspace.colorHex ?? workspace.color ?? null;
        const selectedHex = resolveColorToHex(selected);
        if (!selectedHex) {
            return [null, null];
        }

        const [h, s, l] = hexToHSL(selectedHex);
        //const secondary = HSLToHex([(h + 150) % 360, s, l]);
        const secondary = HSLToHex([(h + 25) % 360, s * 0.65, Math.min(100, l + 35) ]);

        return [selectedHex, secondary]
    }, [workspace?.brandColor, workspace?.colorHex, workspace?.color]);

    const { accentClasses, secondaryClasses } = useMemo(() => {
        if (accentColor) {
            
            return {
                accentClasses: ensureDynamicAccentClasses(accentColor),
                secondaryClasses: ensureDynamicAccentClasses(secondaryColor)
            }
        }
        return {
            accentClasses: null,
            secondaryClasses: null
        }
    },
        [accentColor]
    );

    const [accentButtonClassName, secondaryButtonClassName] = useMemo(() => {
        const accent = accentClasses ?  `${accentClasses.base} ${accentClasses.border} border` : DEFAULT_BUTTON_CLASSES
        const secondary = secondaryClasses ?  `${secondaryClasses.base} ${secondaryClasses.border} border` : DEFAULT_BUTTON_CLASSES
        return [accent, secondary]
    }, [accentClasses]);

    const workspaceLogo = useMemo(() => {
        if (workspace?.logoUrl) {
            return (
                <Avatar
                    isBordered={logoSizeVariant.isBordered}
                    className={`${logoSizeVariant.container} ${logoSizeVariant.avatarBorder} ${logoSizeVariant.avatarFrame}`}
                    radius={logoSizeVariant.radius}
                    src={workspace.logoUrl}
                />
            );
        }
        return (
            <div className={`flex ${logoSizeVariant.container} items-center justify-center ${logoSizeVariant.fallbackRadius} border border-white/40 bg-white/90 shadow-lg backdrop-blur`}>
                <Logo className={`${logoSizeVariant.icon} text-slate-600`} />
            </div>
        );
    }, [workspace?.logoUrl, logoSizeVariant]);

    return {
        workspaceGradient,
        workspaceGradientValues,
        accentColor,
        accentClasses,
        accentButtonClassName,
        accentBaseClassName: accentClasses?.base ?? "bg-background",
        accentPanelClassName: accentClasses?.faded ?? "bg-background",
        accentTextClassName: accentClasses?.text ?? "",
        accentSoftTextClassName: accentClasses?.softText ?? "text-white/70",
        accentTrackClassName: accentClasses?.track ?? "",
        accentBorderClassName: accentClasses?.border ?? "border-slate-200",
        accentIndicatorClassName: accentClasses?.indicator ?? "bg-primary",
        accentContrastClassName: accentClasses?.contrast ?? "text-white",
        secondaryColor,
        secondaryClasses,
        secondaryButtonClassName,
        secondaryBaseClassName: secondaryClasses?.base ?? "bg-background",
        secondaryPanelClassName: secondaryClasses?.faded ?? "bg-background",
        secondaryTextClassName: secondaryClasses?.text ?? "",
        secondarySoftTextClassName: secondaryClasses?.softText ?? "text-white/70",
        secondaryTrackClassName: secondaryClasses?.track ?? "",
        secondaryBorderClassName: secondaryClasses?.border ?? "border-slate-200",
        secondaryIndicatorClassName: secondaryClasses?.indicator ?? "bg-primary",
        secondaryContrastClassName: secondaryClasses?.contrast ?? "text-white",
        workspaceLogo,
    };
}
